import { useEffect, useRef, useState } from "react";
import { Globe, Check } from "lucide-react";
import { useLang, LANGUAGES } from "../../contexts/LanguageContext";

/**
 * Compact language selector shown in the Header. Renders a dropdown with the
 * four supported languages. Spanish is the default; Arabic is RTL.
 * Pass `align="left"` when placed in the mobile drawer (opens rightward).
 */
export default function LangSwitcher({ testId = "lang-switcher", align = "right" }) {
  const { lang, setLang, languages } = useLang();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const list = languages || LANGUAGES;
  const current = list.find((l) => l.code === lang);

  useEffect(() => {
    const close = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  return (
    <div ref={wrapRef} className="relative" data-testid={testId}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 hover:text-cecit-gold transition-colors uppercase whitespace-nowrap"
        data-testid={`${testId}-button`}
      >
        <Globe className="w-3 h-3" /> {current?.short || "ES"}
      </button>
      {open && (
        <ul
          role="listbox"
          className={`absolute top-full mt-1 min-w-[9.5rem] bg-white text-[#0F2040] border border-gray-200 shadow-lg z-50 ${
            align === "left" ? "left-0" : "right-0"
          }`}
          data-testid={`${testId}-menu`}
        >
          {list.map((l) => (
            <li key={l.code}>
              <button
                type="button"
                role="option"
                aria-selected={l.code === lang}
                onClick={() => { setLang(l.code); setOpen(false); }}
                dir={l.dir}
                data-testid={`${testId}-option-${l.code}`}
                className={`w-full text-left px-3 py-2 text-xs flex items-center justify-between gap-3 normal-case hover:bg-gray-50 ${
                  l.code === lang ? "bg-[#FCEAED] text-[#C8102E] font-medium" : ""
                }`}
              >
                <span>{l.label}</span>
                {l.code === lang && <Check className="w-3.5 h-3.5" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
